import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Plus, SlidersHorizontal, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { API, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useOutlet } from "../context/OutletContext";
import Products from "./Products";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbLd } from "../components/Breadcrumbs";
import { SITE_URL } from "../lib/seo";

const BASE = "https://static.prod-images.emergentagent.com/jobs/55f634fb-7cfc-429a-9e9b-faaf578e2dbd/images/";
const img = (id) => BASE + id;

const CONFIGS = {
  "intimates": {
    name: "Zeya Lingerie",
    categories: ["Zeya Lingerie"],
    accent: "#C05A6E",
    logo: "https://customer-assets-jt897jd0.emergentagent.net/job_suvikka-outlet-hub/artifacts/05u2cqgj_Zeya%20Logo.png",
    seo: { title: "Zeya Lingerie Online | Bras, Nightwear & Intimates — Suvikka", desc: "Shop Zeya Lingerie at Suvikka — bras, panties, lace sets, satin nightwear and robes. Feel Confident, Always. Premium intimates at honest prices." },
    hero: { image: "https://images.unsplash.com/photo-1709422122379-79776f76bd87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", eyebrow: "Zeya Lingerie", heading: "Feel Confident, Always", sub: "Beautifully crafted lingerie & nightwear, made for every woman.", cta: "Shop Zeya" },
    subcats: [
      ["Bras", "bra|bralette", "https://images.unsplash.com/photo-1709422122379-79776f76bd87?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"],
      ["Panties", "panty|brief|hipster", "https://images.unsplash.com/photo-1606245455144-2243f0b4d97b?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"],
      ["Lingerie Sets", "set", "https://images.unsplash.com/photo-1568663521381-33b7c467fda0?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"],
      ["Nightwear", "night|slip|gown", "https://images.unsplash.com/photo-1629936060120-e38e75bb2784?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"],
      ["Robes", "robe", "https://images.unsplash.com/photo-1742569182304-d79d435c7711?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"],
    ],
    promo: { image: "https://images.unsplash.com/photo-1606245455144-2243f0b4d97b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", title: "Zeya — Feel Confident, Always", sub: "Everyday comfort meets elegant design — thoughtfully crafted intimates for every woman." },
  },
  "jewellery": {
    name: "Jewellery",
    categories: ["Jewellery"],
    accent: "#B8860B",
    seo: { title: "Fashion Jewellery Online | Necklaces, Earrings & Sets — Suvikka", desc: "Shop premium fashion jewellery at Suvikka — gold-plated necklaces, jhumka earrings, bangles, rings and ethnic sets. Trending designs at affordable prices." },
    hero: { image: img("492fcf803e88041cb5510469b5b106044c285a5a9832a85c034c5e60d034c1c6.jpeg"), eyebrow: "Suvikka Jewellery", heading: "Your Everyday Sparkle", sub: "Trending jewellery starting at ₹49", cta: "Shop Now" },
    subcats: [
      ["Earrings", "earring|jhumka", "f7975b1864d6ce608060c15f0acfa0c78f11c37654ec889d87ee0a8922a7ad0f.jpeg"],
      ["Necklaces & Chains", "necklace|chain", "202a2c271c094c711426b85bda4c91dbb58b7c166f894818aaa7845fe93de39d.jpeg"],
      ["Bangles & Bracelets", "bangle|bracelet|kada", "202a2c271c094c711426b85bda4c91dbb58b7c166f894818aaa7845fe93de39d.jpeg"],
      ["Rings", "ring", "f7975b1864d6ce608060c15f0acfa0c78f11c37654ec889d87ee0a8922a7ad0f.jpeg"],
      ["Ethnic & Festive", "temple|kundan|bridal|ethnic", "202a2c271c094c711426b85bda4c91dbb58b7c166f894818aaa7845fe93de39d.jpeg"],
      ["Jewellery Sets", "set", "f7975b1864d6ce608060c15f0acfa0c78f11c37654ec889d87ee0a8922a7ad0f.jpeg"],
    ],
    promo: { image: img("50e89019b13a064097969920af444dddab341bd669c244b6e0802a3ec858c93a.jpeg"), title: "Affordable Sparkle, Every Day", sub: "Trend-led fashion jewellery that doesn't cost a fortune — new designs added regularly." },
  },
  "cosmetics": {
    name: "Cosmetics",
    categories: ["Cosmetics", "Skincare"],
    accent: "#D6708B",
    seo: { title: "Beauty & Cosmetics Online | Makeup & Skincare — Suvikka", desc: "Discover Suvikka beauty essentials — makeup, skincare, lips, eyes and face. Premium cosmetics for your everyday glow at affordable prices." },
    hero: { image: img("05dd529e02f62c9254850ab914488db0a7f8e6a6c229fb66dd89c2cc0beb8b85.jpeg"), eyebrow: "Suvikka Beauty", heading: "Your Beauty, Your Rules", sub: "Discover beauty essentials made for your everyday glow.", cta: "Shop Now" },
    subcats: [
      ["Makeup", "makeup|brush|foundation|palette", "992192faae7afd3b7e4d4c810610159d2100c071669230c114d41d0a3a7be0c1.jpeg"],
      ["Skincare", "skincare|serum|cream", "5ccd51032b83105ca13d1bd5200b9a4e9133769b93cf3a96c008491252894213.jpeg"],
      ["Lips", "lip", "dcc98250a620df48d351c9c1aeb2012db8f634ddfc70fbc2ca10011b3a547dc4.jpeg"],
      ["Eyes", "eye|kajal|mascara", "992192faae7afd3b7e4d4c810610159d2100c071669230c114d41d0a3a7be0c1.jpeg"],
      ["Face", "foundation|compact|face|bb", "d8e5bc28a092b7db4ba3d94caa649c334faeb24aa7e73c7a93a32787d0089c6c.jpeg"],
      ["Nails", "nail", "dcc98250a620df48d351c9c1aeb2012db8f634ddfc70fbc2ca10011b3a547dc4.jpeg"],
      ["Beauty Essentials", "kit|essential|set", "5ea5fcfd4a410d9657c3025d3ea21d189e4bbab0d2f1378aea8f63cd91791d83.jpeg"],
    ],
    promo: { image: img("ea240941f12164038f3654375135426011a4eaeac27f998f797c4b68b99e5efa.jpeg"), title: "Affordable Beauty Collection", sub: "Everyday glow essentials — quality-checked beauty that fits your budget." },
  },
  "bags": {
    name: "Bags",
    categories: ["Bags"],
    accent: "#A9743F",
    seo: { title: "Handbags & Sling Bags Online — Suvikka", desc: "Shop Suvikka bags — handbags, slings, totes, wallets and mini bags. Everyday fashion bags with premium finish at affordable prices." },
    hero: { image: img("2b6be63a635e305d96e4cc65d935aef90ce2eafbb7dbf83e565f21551dc2b486.jpeg"), eyebrow: "Suvikka Bags", heading: "Carry Your Style", sub: "Everyday bags. Effortless fashion.", cta: "Shop Now" },
    subcats: [
      ["Handbags", "handbag", "8eab5d2bf6cee82beec3716c3247615acea922818e9eb55fa1f9091511bcb1c0.jpeg"],
      ["Shoulder Bags", "shoulder", "bb91a08975fe7c0245a67d5c49f23ce5cffc76b2fb2ac8aed4a4a51015923cdb.jpeg"],
      ["Sling Bags", "sling", "8eab5d2bf6cee82beec3716c3247615acea922818e9eb55fa1f9091511bcb1c0.jpeg"],
      ["Tote Bags", "tote", "bb91a08975fe7c0245a67d5c49f23ce5cffc76b2fb2ac8aed4a4a51015923cdb.jpeg"],
      ["Wallets", "wallet|purse", "8eab5d2bf6cee82beec3716c3247615acea922818e9eb55fa1f9091511bcb1c0.jpeg"],
      ["Mini Bags", "mini", "bb91a08975fe7c0245a67d5c49f23ce5cffc76b2fb2ac8aed4a4a51015923cdb.jpeg"],
      ["Travel Bags", "travel|duffle", "8eab5d2bf6cee82beec3716c3247615acea922818e9eb55fa1f9091511bcb1c0.jpeg"],
    ],
    promo: { image: img("77346d3dd593f4bec0c7f3397fb33bd784f4b530121aa96baab16dbcadca9be2.jpeg"), title: "Bags For Every Outing", sub: "From daily carry to date-night minis — effortless style, honest pricing." },
  },
  "ladies-fashion": {
    name: "Fashion",
    categories: ["Ladies Fashion", "Girls Fashion", "Belts", "Accessories", "Jewellery"],
    accent: "#C05A6E",
    seo: { title: "Women's Fashion & Accessories Online — Suvikka", desc: "Explore Suvikka fashion — dresses, tops, kurtis, jewellery, belts and accessories. Trending looks for every mood at affordable prices." },
    hero: { image: img("62bda3f10d4dca828d89d56b7f84d4cd3cf9b0471f92f613c6c88f4c99533deb.jpeg"), eyebrow: "Suvikka Fashion", heading: "Trending Looks. Suvikka Style.", sub: "Explore fashion and accessories for every mood.", cta: "Shop Now" },
    subcats: [
      ["Dresses", "dress|gown", "c30902820bbf0f71b005d5e4851b304ca0b0cb5867de3b8321d9793031cfb4f1.jpeg"],
      ["Tops", "top|shirt|blouse", "c30902820bbf0f71b005d5e4851b304ca0b0cb5867de3b8321d9793031cfb4f1.jpeg"],
      ["Kurtis", "kurti|kurta|ethnic", "feeb49151ddbb6fedd5631ee37c7412464abbd51c5353f05b8f9e30f347f3b69.jpeg"],
      ["Jewellery", "necklace|earring|jhumka|kundan", "202a2c271c094c711426b85bda4c91dbb58b7c166f894818aaa7845fe93de39d.jpeg"],
      ["Belts", "belt", "ef0a8178a602d8b85104f7fefd326c6785a30d6ebd256654c9427eef0cdc2e43.jpeg"],
      ["Hair Accessories", "hair|clip|scrunchie", "a91f9cea0992e41a6cdc6275c8ea19fcfe2ecda81e4bcba3bb5f638aec681b02.jpeg"],
      ["Fashion Accessories", "sunglass|scarf|watch|combo", "55e6b8e56dedafd88b5506bc04aca5003607dabbbbace1287abb89c88cc809c6.jpeg"],
    ],
    promo: { image: img("36a300be8b42f0d2b22392ea72e539ab727a91d659b4bc1c9053985f58594476.jpeg"), title: "The Seasonal Edit", sub: "Fresh drops across fashion & accessories — curated for what's trending now." },
  },
};

const COLOURS = ["Gold", "Rose Gold", "Silver", "Tan", "Pink", "Black", "Blue", "White", "Indigo"];
const MATERIALS = ["Gold-Plated", "Kundan", "Pearl", "Leather", "Metallic", "Matte"];

function discount(p) { return p.mrp > p.selling_price ? Math.round((1 - p.selling_price / p.mrp) * 100) : 0; }

function Tile({ p, accent }) {
  const { user } = useAuth();
  const { refresh } = useCart();
  const { outlet } = useOutlet();
  const nav = useNavigate();
  const [wished, setWished] = useState(false);
  const off = discount(p);
  const oos = p.outlet_in_stock === false;
  const toggleWish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav("/login"); return; }
    try {
      if (wished) { await API.delete(`/wishlist/${p.id}`); setWished(false); }
      else { await API.post(`/wishlist/${p.id}`); setWished(true); toast.success("Saved to your wishlist"); }
    } catch { toast.error("Could not update wishlist"); }
  };
  const addToCart = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (oos) return;
    if (!user) { nav("/login"); return; }
    try { await API.post(`/cart/${p.id}${outlet ? `?outlet=${outlet.id}` : ""}`); await refresh(); toast.success("Added to bag"); }
    catch { toast.error("Could not add to bag"); }
  };
  return (
    <Link to={`/product/${p.slug || p.id}`} data-testid={`cat-product-${p.sku}`} className="group bg-white border border-[#EADBD8] rounded-2xl overflow-hidden shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all duration-300">
      <div className="relative aspect-[4/5] overflow-hidden bg-stone-100">
        {p.images?.[0] && <img src={p.images[0]} alt={p.title} loading="lazy" className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${oos ? "grayscale opacity-60" : ""}`} />}
        <button onClick={toggleWish} data-testid={`cat-wishlist-${p.sku}`} className={`absolute top-3 right-3 p-2 rounded-full bg-white/90 shadow-sm ${wished ? "text-[#6A1B29]" : "text-stone-500"} hover:text-[#6A1B29]`}>
          <Heart className={`w-4 h-4 ${wished ? "fill-[#6A1B29]" : ""}`} />
        </button>
        {off > 0 && <span className="absolute top-3 left-3 text-white text-[10px] font-bold px-2 py-1" style={{ background: accent }}>{off}% OFF</span>}
        {oos ? <span className="absolute bottom-0 inset-x-0 bg-stone-900/80 text-white text-[10px] font-semibold py-1 text-center uppercase tracking-wide" data-testid={`cat-oos-${p.sku}`}>Out of Stock</span>
          : p.stock_status !== "In Stock" && <span className="absolute bottom-0 inset-x-0 bg-stone-900/80 text-white text-[10px] font-semibold py-1 text-center uppercase tracking-wide">{p.stock_status}</span>}
        {oos ? (
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-stone-300 text-stone-500 text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-not-allowed" data-testid={`cat-quickadd-disabled-${p.sku}`}>Out of Stock</span>
        ) : (
          <button onClick={addToCart} data-testid={`cat-quickadd-${p.sku}`}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all inline-flex items-center gap-1.5 bg-[#6A1B29] text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg whitespace-nowrap">
            <Plus className="w-3.5 h-3.5" /> Quick Add
          </button>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase" style={{ color: accent }}>{p.category}</p>
        <h3 className="text-sm sm:text-base font-medium text-stone-900 mt-1 line-clamp-1">{p.title}</h3>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="text-sm sm:text-base font-semibold text-stone-900">{inr(p.selling_price)}</span>
          {off > 0 && <><span className="text-xs text-stone-400 line-through">{inr(p.mrp)}</span><span className="text-xs font-semibold" style={{ color: accent }}>{off}% off</span></>}
        </div>
      </div>
    </Link>
  );
}

function Row({ title, items, accent, testid }) {
  if (!items.length) return null;
  return (
    <section className="py-10 sm:py-14" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-stone-900">{title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-6">
          {items.slice(0, 4).map((p) => <Tile key={p.id} p={p} accent={accent} />)}
        </div>
      </div>
    </section>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const cfg = CONFIGS[slug];
  const [all, setAll] = useState([]);
  const [sub, setSub] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [colour, setColour] = useState("");
  const [material, setMaterial] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const { outlet } = useOutlet();

  useEffect(() => {
    if (!cfg) return;
    setSub(""); setMaxPrice(""); setColour(""); setMaterial(""); setInStockOnly(false); setSort("newest");
    API.get(`/products?limit=100${outlet ? `&outlet=${outlet.id}` : ""}`).then((r) => setAll(r.data.items)).catch(() => {});
  }, [slug, outlet]);

  const catProducts = useMemo(
    () => (cfg ? all.filter((p) => cfg.categories.includes(p.category)) : []),
    [all, cfg]
  );

  const text = (p) => `${p.title} ${p.description} ${p.subcategory || ""}`.toLowerCase();
  const pcols = (p) => (p.colours?.length ? p.colours : COLOURS.filter((c) => text(p).includes(c.toLowerCase())));
  const pmats = (p) => (p.materials?.length ? p.materials : MATERIALS.filter((m) => text(p).includes(m.toLowerCase())));
  const availColours = useMemo(() => COLOURS.filter((c) => catProducts.some((p) => pcols(p).some((x) => x.toLowerCase() === c.toLowerCase()))), [catProducts]);
  const availMaterials = useMemo(() => MATERIALS.filter((m) => catProducts.some((p) => pmats(p).some((x) => x.toLowerCase() === m.toLowerCase()))), [catProducts]);

  const filtered = useMemo(() => {
    let list = [...catProducts];
    if (sub) { const re = new RegExp(sub, "i"); list = list.filter((p) => re.test(text(p))); }
    if (maxPrice) list = list.filter((p) => p.selling_price <= Number(maxPrice));
    if (colour) list = list.filter((p) => pcols(p).some((x) => x.toLowerCase() === colour.toLowerCase()));
    if (material) list = list.filter((p) => pmats(p).some((x) => x.toLowerCase() === material.toLowerCase()));
    if (inStockOnly) list = list.filter((p) => (outlet ? p.outlet_in_stock : p.stock_status === "In Stock"));
    if (sort === "price_asc") list.sort((a, b) => a.selling_price - b.selling_price);
    else if (sort === "price_desc") list.sort((a, b) => b.selling_price - a.selling_price);
    else if (sort === "bestselling") list.sort((a, b) => (b.trending - a.trending) || (discount(b) - discount(a)));
    else list.sort((a, b) => (b.new_arrival - a.new_arrival) || String(b.created_at).localeCompare(String(a.created_at)));
    return list;
  }, [catProducts, sub, maxPrice, colour, material, inStockOnly, sort, outlet]);

  if (!cfg) return <Products key="cat" />;

  const trending = catProducts.filter((p) => p.trending);
  const arrivals = catProducts.filter((p) => p.new_arrival);
  const best = [...catProducts].sort((a, b) => discount(b) - discount(a));
  const A = cfg.accent;
  const catPath = slug === "trending-now" ? "/trending" : `/category/${slug}`;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Categories", path: "/categories" }, { name: cfg.name }];
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cfg.seo.title,
    description: cfg.seo.desc,
    url: `${SITE_URL}${catPath}`,
  };

  const FilterControls = () => (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-3">Category</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSub("")} data-testid="filter-sub-all" className={`text-xs px-3 py-1.5 rounded-full border ${!sub ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700"}`}>All</button>
          {cfg.subcats.map(([label, kw]) => (
            <button key={label} onClick={() => setSub(sub === kw ? "" : kw)} data-testid={`filter-sub-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${sub === kw ? "text-white" : "border-stone-300 text-stone-700"}`} style={sub === kw ? { background: A, borderColor: A } : {}}>{label}</button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-3">Price</p>
        <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="filter-price" className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm">
          <option value="">Any price</option><option value="500">Under ₹500</option><option value="1000">Under ₹1,000</option><option value="2000">Under ₹2,000</option><option value="5000">Under ₹5,000</option>
        </select>
      </div>
      {availColours.length > 0 && (
        <div>
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-3">Colour</p>
          <div className="flex flex-wrap gap-2">
            {availColours.map((c) => (
              <button key={c} onClick={() => setColour(colour === c ? "" : c)} data-testid={`filter-colour-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${colour === c ? "text-white" : "border-stone-300 text-stone-700"}`} style={colour === c ? { background: A, borderColor: A } : {}}>{c}</button>
            ))}
          </div>
        </div>
      )}
      {availMaterials.length > 0 && (
        <div>
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-3">Material</p>
          <div className="flex flex-wrap gap-2">
            {availMaterials.map((m) => (
              <button key={m} onClick={() => setMaterial(material === m ? "" : m)} data-testid={`filter-material-${m.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${material === m ? "text-white" : "border-stone-300 text-stone-700"}`} style={material === m ? { background: A, borderColor: A } : {}}>{m}</button>
            ))}
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-stone-700" data-testid="filter-instock">
        <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} /> In stock only
      </label>
    </div>
  );

  return (
    <div>
      <Seo title={cfg.seo.title} description={cfg.seo.desc} canonicalPath={catPath} type="website" image={cfg.hero.image} jsonLd={[breadcrumbLd(crumbs), collectionLd]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5" data-testid="category-breadcrumbs"><Breadcrumbs items={crumbs} accent={A} /></div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F3EDE7]" data-testid="category-hero">
        <img src={cfg.hero.image} alt={`Suvikka ${cfg.name}`} className="w-full h-[42vh] sm:h-[52vh] lg:h-[60vh] object-cover" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 sm:px-10 lg:px-16">
            <div className="max-w-md ml-auto text-right sm:pr-4">
              {cfg.logo && <img src={cfg.logo} alt={cfg.name} className="h-20 sm:h-24 ml-auto mb-3 rounded-xl bg-white/85 p-2 shadow-lg reveal" data-testid="category-brand-logo" />}
              <p className="text-xs font-semibold tracking-[0.25em] uppercase reveal" style={{ color: A }}>{cfg.hero.eyebrow}</p>
              <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold text-stone-900 mt-2 leading-tight reveal reveal-1" data-testid="category-hero-heading">{cfg.hero.heading}</h1>
              <p className="text-sm sm:text-lg text-stone-700 mt-3 reveal reveal-2">{cfg.hero.sub}</p>
              <a href="#listing" data-testid="category-hero-cta" className="inline-flex items-center gap-2 mt-6 text-white font-semibold px-7 py-3 rounded-full transition-transform hover:scale-105 reveal reveal-3" style={{ background: A }}>
                {cfg.hero.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="py-12 sm:py-16" data-testid="shop-by-category">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-stone-900 text-center">Shop by Category</h2>
          <div className="flex gap-5 sm:gap-8 mt-8 overflow-x-auto pb-3 sm:justify-center scrollbar-hide">
            {cfg.subcats.map(([label, kw, im]) => (
              <button key={label} onClick={() => { setSub(kw); document.getElementById("listing")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`shop-cat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex flex-col items-center gap-3 shrink-0 group">
                <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full overflow-hidden border-2 border-transparent group-hover:border-current transition-colors" style={{ color: A }}>
                  <img src={im.startsWith("http") ? im : img(im)} alt={label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-stone-800 text-center w-24 leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Row title={`Trending ${cfg.name}`} items={trending} accent={A} testid="row-trending" />
      <div className="bg-[#FAF6F1]"><Row title="New Arrivals" items={arrivals} accent={A} testid="row-new" /></div>
      <Row title="Best Sellers" items={best} accent={A} testid="row-best" />

      {/* PROMO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="category-promo">
        <div className="relative overflow-hidden rounded-2xl">
          <img src={cfg.promo.image} alt="" className="w-full h-40 sm:h-56 object-cover" loading="lazy" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-6 sm:px-12 max-w-lg">
              <h3 className="font-display text-2xl sm:text-4xl font-semibold text-stone-900">{cfg.promo.title}</h3>
              <p className="text-sm sm:text-base text-stone-700 mt-2">{cfg.promo.sub}</p>
              <a href="#listing" className="inline-flex items-center gap-2 mt-4 text-white text-sm font-semibold px-6 py-2.5 rounded-full" style={{ background: A }} data-testid="promo-cta">Explore <ArrowRight className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </section>

      {/* LISTING */}
      <section id="listing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="category-listing">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">All {cfg.name}</h2>
            <p className="text-sm text-stone-500 mt-1" data-testid="listing-count">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-toggle" className="lg:hidden inline-flex items-center gap-1.5 border border-stone-300 rounded-full px-4 py-2 text-sm font-medium">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="border border-stone-300 rounded-full px-4 py-2 text-sm">
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="bestselling">Best Selling</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="hidden lg:block" data-testid="filter-sidebar"><FilterControls /></aside>
          <div className="lg:col-span-3">
            {filtered.length === 0 ? (
              <p className="text-stone-500 bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="listing-empty">No products match these filters yet. Try clearing a filter or explore another category.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="listing-grid">
                {filtered.map((p) => <Tile key={p.id} p={p} accent={A} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MOBILE FILTER DRAWER */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-stone-900/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display text-xl font-semibold">Filters</p>
              <button onClick={() => setShowFilters(false)} data-testid="close-filter-drawer"><X className="w-5 h-5" /></button>
            </div>
            <FilterControls />
            <button onClick={() => setShowFilters(false)} className="w-full mt-8 text-white font-semibold py-3 rounded-full" style={{ background: A }} data-testid="apply-filters">Show {filtered.length} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
